/* ============================================================
   zh-article.js (refactor v2)
   Frontend behaviour für das Zahnheld Magazin Article Template.

   Pipeline:
   1. Slugify + chunk-num auf jeder H2 in .zh-art-body__content
   2. Lead-Detection: erste 1-3 P vor erster H2 in TL;DR-Box verschieben
      (nur wenn TL;DR-Section data-zh-tldr-auto Flag hat)
   3. FAQ-Auto-Detect: H2 mit "FAQ" + nachfolgende H3 + P werden zu
      <details>-Accordion + JSON-LD FAQPage injiziert
   4. TOC bauen aus H2s, plus Scroll-Spy via IntersectionObserver

   Vanilla JS, keine Dependencies, keine jQuery.
   ============================================================ */
(function () {
  'use strict';

  function clearChildren(el) {
    while (el.firstChild) el.removeChild(el.firstChild);
  }

  function slugify(text) {
    return text
      .trim()
      .toLowerCase()
      .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /* Check if an element is visually empty (whitespace only or just a <br>).
     RTE-Editoren erzeugen oft <p></p>, <p>&nbsp;</p>, <p><br></p> als Leerzeilen. */
  function isVisuallyEmpty(el) {
    if (!el) return false;
    /* ws-blog-text-image placeholders are empty before hydration but carry
       product data (data-ws-*) that buildBlogProductBlocks renders later. Never
       treat them as empty, otherwise trimEmptyBeforeHeadings would delete a
       product box that sits directly before a heading. */
    if (el.classList && el.classList.contains('ws-blog-text-image')) return false;
    var tag = el.tagName;
    if (tag === 'BR') return true;
    if (tag === 'P' || tag === 'DIV') {
      /* If children are only BR or whitespace-only text, it is empty */
      var hasContent = false;
      for (var i = 0; i < el.childNodes.length; i++) {
        var node = el.childNodes[i];
        if (node.nodeType === 1 && node.tagName !== 'BR') {
          hasContent = true;
          break;
        }
        if (node.nodeType === 3) {
          var t = (node.textContent || '').replace(/ /g, ' ').trim();
          if (t !== '') { hasContent = true; break; }
        }
      }
      return !hasContent;
    }
    return false;
  }

  /* ----- "Du möchtest mehr zum Thema..."-Block aus dem Body
     entfernen. Die Liquid-Seite (snippets/zh-related-cards.liquid)
     liest denselben Block server-side und nutzt die verlinkten
     Artikel als Datengrundlage für die Auch-lesenswert-Section, mit
     vollem Article-Objekt (Bild, Datum, Tags). Wir müssen den Block
     hier nur noch aus dem sichtbaren Body entfernen damit er nicht
     im FAQ oder Lesefluss hängt. ----- */
  function removeRelatedOverrideBlock(body) {
    /* WICHTIG: Diese Regex muss synchron mit der trigger_phrases-Liste
       in snippets/zh-related-cards.liquid bleiben. Wenn hier neue
       Trigger-Phrasen hinzukommen, dort ebenfalls anpassen, sonst
       entfernt das JS den Block aus dem Body während Liquid keine
       Article-Override-Cards rendert (inkonsistente UX). */
    var pattern = /m[oö]chtest (du )?(noch )?mehr|mehr zum (thema|kapitel)|weitere artikel|auch interessant/i;
    var paragraphs = body.querySelectorAll('p');
    for (var i = 0; i < paragraphs.length; i++) {
      var p = paragraphs[i];
      var text = (p.textContent || '').trim();
      if (!text || !pattern.test(text)) continue;

      var ul = p.nextElementSibling;
      while (ul && (ul.tagName === 'BR' || isVisuallyEmpty(ul))) {
        ul = ul.nextElementSibling;
      }
      if (!ul || (ul.tagName !== 'UL' && ul.tagName !== 'OL')) continue;

      var links = ul.querySelectorAll('a[href]');
      if (links.length === 0) continue;

      p.remove();
      ul.remove();
      return true;
    }
    return false;
  }

  /* ----- Step 0: Trim empty siblings before headings ----- */
  function trimEmptyBeforeHeadings(body) {
    var headings = body.querySelectorAll('h1, h2, h3, h4');
    headings.forEach(function (h) {
      var prev = h.previousElementSibling;
      while (prev && isVisuallyEmpty(prev)) {
        var toRemove = prev;
        prev = prev.previousElementSibling;
        toRemove.remove();
      }
    });
  }

  /* ----- Step 1: Slugify + chunk-num on all H2s ----- */
  function assignChunkNumbers(body) {
    var headings = Array.prototype.slice.call(body.querySelectorAll(':scope > h2'));
    var slugCounts = {};
    headings.forEach(function (h2, index) {
      var num = String(index + 1).padStart(2, '0');
      h2.setAttribute('data-chunk-num', num);
      if (!h2.id) {
        var slug = slugify(h2.textContent || ('section-' + (index + 1)));
        if (!slug) slug = 'section-' + (index + 1);
        if (slugCounts[slug] != null) {
          slugCounts[slug] += 1;
          slug = slug + '-' + slugCounts[slug];
        } else {
          slugCounts[slug] = 0;
        }
        h2.id = slug;
      }
    });
    return headings;
  }

  /* ----- Step 2: Promote lead elements before first H2 into TL;DR ----- */
  function promoteLeadToTldr(body) {
    var tldrSection = document.querySelector('[data-zh-tldr-auto]');
    if (!tldrSection) return false;

    var answer = tldrSection.querySelector('.zh-art-tldr__answer');
    if (!answer) return false;

    /* Collect lead elements before first H2 (P, UL, OL, H3, H4 etc.),
       skip scripts/styles. */
    var leadElements = [];
    var node = body.firstElementChild;
    var SKIP = { SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, LINK: 1, META: 1 };
    while (node && node.tagName !== 'H2') {
      if (!SKIP[node.tagName]) {
        leadElements.push(node);
      }
      node = node.nextElementSibling;
    }

    if (leadElements.length === 0) {
      tldrSection.style.display = 'none';
      return false;
    }

    /* If the first lead element is an H3/H4, promote it to be the label
       so we avoid the default "Antwort in 30 Sekunden" + duplicate headline. */
    var firstEl = leadElements[0];
    if (firstEl && (firstEl.tagName === 'H3' || firstEl.tagName === 'H4')) {
      var label = tldrSection.querySelector('.zh-art-tldr__label');
      if (label) label.textContent = firstEl.textContent.trim();
      firstEl.remove();
      leadElements.shift();
    }

    if (leadElements.length === 0) {
      tldrSection.style.display = 'none';
      return false;
    }

    leadElements.forEach(function (el) {
      answer.appendChild(el);
    });
    tldrSection.removeAttribute('data-zh-tldr-auto');
    return true;
  }

  /* ----- Step 3: FAQ-Auto-Detect ----- */
  function buildFaqFromBody(body, headings) {
    /* Find first H2 with text containing "FAQ" (case insensitive) */
    var faqHeading = null;
    for (var i = 0; i < headings.length; i++) {
      var t = (headings[i].textContent || '').toLowerCase();
      if (t.indexOf('faq') !== -1 || headings[i].hasAttribute('data-faq')) {
        faqHeading = headings[i];
        break;
      }
    }
    if (!faqHeading) return;

    /* Collect siblings between this H2 and next H2 */
    var faqNodes = [];
    var node = faqHeading.nextElementSibling;
    while (node && node.tagName !== 'H2') {
      faqNodes.push(node);
      node = node.nextElementSibling;
    }
    if (faqNodes.length === 0) return;

    /* Group H3 + following P's into Q/A pairs */
    var qaPairs = [];
    var currentQ = null;
    var currentAs = [];
    faqNodes.forEach(function (n) {
      if (n.tagName === 'H3') {
        if (currentQ) qaPairs.push({ question: currentQ, answers: currentAs });
        currentQ = n;
        currentAs = [];
      } else if (currentQ) {
        currentAs.push(n);
      }
    });
    if (currentQ) qaPairs.push({ question: currentQ, answers: currentAs });

    if (qaPairs.length === 0) return;

    /* Build details/summary structure */
    var jsonLdItems = [];
    qaPairs.forEach(function (pair) {
      var details = document.createElement('details');
      details.className = 'zh-art-faq__item';

      var summary = document.createElement('summary');
      summary.textContent = pair.question.textContent.trim();
      details.appendChild(summary);

      var answerDiv = document.createElement('div');
      answerDiv.className = 'zh-art-faq__answer';
      pair.answers.forEach(function (a) {
        answerDiv.appendChild(a.cloneNode(true));
      });
      details.appendChild(answerDiv);

      /* Insert details before the question, then remove originals */
      pair.question.parentNode.insertBefore(details, pair.question);
      pair.question.remove();
      pair.answers.forEach(function (a) { a.remove(); });

      jsonLdItems.push({
        '@type': 'Question',
        name: pair.question.textContent.trim(),
        acceptedAnswer: {
          '@type': 'Answer',
          text: pair.answers.map(function (a) { return a.textContent.trim(); }).join(' ')
        }
      });
    });

    /* Inject JSON-LD */
    var script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: jsonLdItems
    });
    document.head.appendChild(script);
  }

  /* ----- ws-blog-text-image Inline-Produkt-Block -----
     Alte Blog-Artikel enthalten leere <div class="ws-blog-text-image"
     data-ws-*> Platzhalter. Im Standard-Theme befuellt sie ein eigenes
     Snippet (snippets/blog-snippets.liquid) clientseitig. Das zh-magazin
     Template laedt dieses Snippet nicht, daher blieben die Bloecke leer.
     Hier bauen wir sie im Magazin-Design nach. */
  function makeCheckIcon() {
    var ns = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', '0 0 48 48');
    svg.setAttribute('width', '20');
    svg.setAttribute('height', '20');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    var path = document.createElementNS(ns, 'path');
    path.setAttribute('d', 'M18.9 35.7 7.7 24.5l2.15-2.15 9.05 9.05 19.2-19.2 2.15 2.15Z');
    svg.appendChild(path);
    return svg;
  }

  function buildBlogProductBlocks(scope) {
    var blocks = (scope || document).querySelectorAll('.ws-blog-text-image');
    blocks.forEach(function (block) {
      if (block.getAttribute('data-zh-built') === '1') return;
      var d = block.dataset;
      block.setAttribute('data-zh-built', '1');
      block.classList.add('zh-art-inline-cta');
      if (d.wsImage) block.classList.add('has-media');

      /* Platzhalter-Div ist im RTE oft nicht wirklich leer, sondern
         enthaelt ein Whitespace-/nbsp-Textnode. Im Grid wird daraus ein
         anonymes Grid-Item in Zelle 1, das Bild + Text in die falschen
         Spalten schiebt. Darum vor dem Aufbau alle Kindknoten entfernen. */
      while (block.firstChild) block.removeChild(block.firstChild);

      var frag = document.createDocumentFragment();

      if (d.wsImage) {
        var media = document.createElement('div');
        media.className = 'zh-art-inline-cta__media';
        var img = document.createElement('img');
        img.src = d.wsImage;
        img.alt = d.wsImageAlt || '';
        img.loading = 'lazy';
        img.decoding = 'async';
        media.appendChild(img);
        frag.appendChild(media);
      }

      var copy = document.createElement('div');
      copy.className = 'zh-art-inline-cta__body';

      var eyebrow = document.createElement('p');
      eyebrow.className = 'zh-art-inline-cta__eyebrow';
      // Translated via blog.post.product_recommendation (exposed by js-variables);
      // literal fallback in case themeVariables is unavailable.
      eyebrow.textContent =
        (window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.productRecommendation) ||
        'Produktempfehlung';
      copy.appendChild(eyebrow);

      if (d.wsTitle) {
        var title = document.createElement('p');
        title.className = 'zh-art-inline-cta__title';
        title.textContent = d.wsTitle;
        copy.appendChild(title);
      }

      var bullets = [d.wsBulletFirst, d.wsBulletSecond, d.wsBulletThird]
        .filter(function (b) { return b && b.trim() !== ''; });
      if (bullets.length) {
        var ul = document.createElement('ul');
        ul.className = 'zh-art-inline-cta__list';
        bullets.forEach(function (text) {
          var li = document.createElement('li');
          var icon = document.createElement('span');
          icon.className = 'zh-art-inline-cta__check';
          icon.appendChild(makeCheckIcon());
          var label = document.createElement('span');
          label.textContent = text;
          li.appendChild(icon);
          li.appendChild(label);
          ul.appendChild(li);
        });
        copy.appendChild(ul);
      }

      if (d.wsButtonText && d.wsButtonLink) {
        var a = document.createElement('a');
        a.className = 'zh-art-btn is-secondary zh-art-inline-cta__btn';
        /* URL-Protokoll-Allowlist: nur http(s)/mailto/tel/relative Pfade.
           data-ws-button-link wird vom RTE-Editor gesetzt und ist daher als
           untrusted zu behandeln (javascript:/data:/vbscript: ausschliessen). */
        var safeHref = '#';
        try {
          var u = new URL(d.wsButtonLink, location.origin);
          if (u.protocol === 'http:' || u.protocol === 'https:' || u.protocol === 'mailto:' || u.protocol === 'tel:') {
            safeHref = d.wsButtonLink;
          }
        } catch (_) { /* invalid URL → bleibt '#' */ }
        a.href = safeHref;
        a.appendChild(document.createTextNode(d.wsButtonText + ' '));
        var arrow = document.createElement('span');
        arrow.className = 'zh-art-btn__arrow';
        arrow.setAttribute('aria-hidden', 'true');
        arrow.textContent = '→';
        a.appendChild(arrow);
        copy.appendChild(a);
      }

      frag.appendChild(copy);
      block.appendChild(frag);
    });
  }

  /* ----- Step 4: TOC + Scroll-Spy -----
     Selektiert alle TOC-Listen via data-zh-toc-list damit mehrere
     TOC-Instanzen pro Seite (sollte selten sein, aber möglich)
     funktionieren statt nur die erste via hardcoded id. Wenn keine
     H2s gefunden werden, wird die TOC-Section komplett versteckt
     statt nur die OL. */
  function buildToc(headings) {
    var tocLists = document.querySelectorAll('[data-zh-toc-list]');
    if (tocLists.length === 0) return;

    if (headings.length === 0) {
      document.querySelectorAll('[data-zh-toc-section]').forEach(function (sec) {
        sec.style.display = 'none';
      });
      return;
    }

    tocLists.forEach(function (tocList) {
      clearChildren(tocList);
      var frag = document.createDocumentFragment();
      headings.forEach(function (h2) {
        var li = document.createElement('li');
        li.setAttribute('data-target', h2.id);
        var a = document.createElement('a');
        a.href = '#' + h2.id;
        a.textContent = h2.textContent.trim();
        li.appendChild(a);
        frag.appendChild(li);
      });
      tocList.appendChild(frag);

      tocList.addEventListener('click', function (event) {
        var link = event.target.closest('a[href^="#"]');
        if (!link) return;
        var target = document.getElementById(link.getAttribute('href').slice(1));
        if (!target) return;
        event.preventDefault();
        var top = target.getBoundingClientRect().top + window.pageYOffset - 24;
        window.scrollTo({ top: top, behavior: 'smooth' });
        history.pushState(null, '', '#' + target.id);
      });

      if ('IntersectionObserver' in window) {
        var items = tocList.querySelectorAll('li');
        var byId = {};
        items.forEach(function (li) { byId[li.getAttribute('data-target')] = li; });

        var observer = new IntersectionObserver(function (entries) {
          var visible = entries
            .filter(function (e) { return e.isIntersecting; })
            .sort(function (a, b) { return a.boundingClientRect.top - b.boundingClientRect.top; });
          if (visible.length === 0) return;
          var topId = visible[0].target.id;
          items.forEach(function (li) { li.classList.remove('is-active'); });
          if (byId[topId]) byId[topId].classList.add('is-active');
        }, {
          rootMargin: '-15% 0% -70% 0%',
          threshold: 0
        });
        headings.forEach(function (h2) { observer.observe(h2); });
      }
    });
  }

  /* ----- Scroll-Progress-Bar ----- */
  function setupProgressBar() {
    /* Wir messen den Fortschritt von 'Hero-Top' bis 'Article-Footer-Bottom',
       damit die Bar erst am Article-Start sichtbar fortschreitet und am
       Ende exakt 100% erreicht. */
    var hero = document.querySelector('.zh-art-hero');
    /* querySelector liefert das ERSTE Match in DOM-Reihenfolge (vor v6
       war das .zh-art-body, also viel zu frueh). Hier wollen wir das
       LETZTE: querySelectorAll plus Index-Length. So erreicht die
       Progress-Bar erst am echten Ende des Artikels 100%. */
    var sectionCandidates = document.querySelectorAll('.zh-art-related, .zh-art-author, .zh-art-product, .zh-art-news, .zh-art-body');
    var lastSection = sectionCandidates.length ? sectionCandidates[sectionCandidates.length - 1] : null;
    if (!hero) return;

    var existing = document.querySelector('.zh-art-progress');
    if (existing) existing.remove();

    var wrap = document.createElement('div');
    wrap.className = 'zh-art-progress';
    var bar = document.createElement('div');
    bar.className = 'zh-art-progress__bar';
    wrap.appendChild(bar);
    document.body.appendChild(wrap);

    function update() {
      var startY = hero.getBoundingClientRect().top + window.pageYOffset;
      var endY = (lastSection || hero).getBoundingClientRect().bottom + window.pageYOffset;
      var viewportH = window.innerHeight;
      var total = Math.max(endY - startY - viewportH, 1);
      var current = Math.min(Math.max(window.pageYOffset - startY, 0), total);
      var pct = (current / total) * 100;
      bar.style.width = pct + '%';
    }

    var ticking = false;
    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(function () { update(); ticking = false; });
        ticking = true;
      }
    }, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ----- News-Ticker Marquee: dynamische Duration aus group.scrollWidth
     und Pixel-pro-Sekunde-Setting damit die Scroll-Geschwindigkeit
     konstant in px/s bleibt unabhängig von Anzahl/Länge der Blocks. */
  function setupTickerMarquee() {
    var tickers = document.querySelectorAll('[data-zh-ticker]');
    if (!tickers.length) return;

    tickers.forEach(function (ticker) {
      var group = ticker.querySelector('[data-zh-ticker-group]');
      if (!group) return;
      var speed = parseFloat(ticker.getAttribute('data-speed')) || 80;

      function measure() {
        var w = group.scrollWidth;
        if (!w) return;
        var duration = w / speed;
        ticker.style.setProperty('--zh-ticker-duration', duration + 's');
      }
      measure();

      if ('ResizeObserver' in window) {
        var ro = new ResizeObserver(function () { measure(); });
        ro.observe(group);
      } else {
        window.addEventListener('resize', measure);
      }

      /* Fonts können erst nach DOMContentLoaded laden, dann wird der
         Group breiter. Sobald Fonts ready sind, neu vermessen. */
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(measure).catch(function(){});
      }
    });
  }

  /* ----- Init ----- */
  function init() {
    /* Global guard: jede zh-article-* Section laedt dieses Script via
       <script src=...>. Mehrere Script-Tags führen zu mehrfacher
       Ausführung von init() und damit zu doppelten Event-Listenern /
       IntersectionObservern. Flag auf window verhindert das. */
    if (window.__zhArticleInitialized) return;
    window.__zhArticleInitialized = true;

    setupTickerMarquee();

    var body = document.querySelector('.zh-art-body__content');
    if (!body) return;

    trimEmptyBeforeHeadings(body);
    /* "Du möchtest mehr"-Block aus dem Body entfernen damit er nicht
       im FAQ oder Lesefluss hängt. Liquid liest denselben Block
       server-side und rendert die verlinkten Artikel in der
       Auch-lesenswert-Section. */
    removeRelatedOverrideBlock(body);

    /* promoteLeadToTldr must run BEFORE chunk numbering, because moving
       lead elements out of body changes which P comes first etc. */
    promoteLeadToTldr(body);
    var headings = assignChunkNumbers(body);
    buildFaqFromBody(body, headings);
    buildBlogProductBlocks(body);
    buildToc(headings);
    setupProgressBar();
  }

  // Internal pure/near-pure helpers exposed for unit tests only.
  window.__zhArticle = {
    slugify: slugify,
    isVisuallyEmpty: isVisuallyEmpty,
    assignChunkNumbers: assignChunkNumbers
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
